import axios from 'axios';
import type { 
  Task, 
  CreateTaskInput, 
  UpdateTaskInput, 
  TaskQueryParams, 
  AlarmInfo 
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Log API configuration in development
if (import.meta.env.DEV) {
  console.log('API Configuration:', {
    VITE_API_URL: import.meta.env.VITE_API_URL,
    API_URL,
    mode: import.meta.env.MODE,
  });
}

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
  withCredentials: true, // Include credentials for CORS
});

// Request interceptor for debugging
apiClient.interceptors.request.use(
  (config) => {
    if (import.meta.env.DEV) {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    if (import.meta.env.DEV) {
      console.log(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }
    return response;
  },
  (error) => {
    // Enhanced error logging
    if (error.response) {
      // Server responded with error status
      console.error('API Error Response:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url,
      });
    } else if (error.request) {
      // Request made but no response received
      console.error('API Network Error:', {
        message: 'No response from server',
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        hint: 'Check if backend server is running on ' + API_URL,
      });
    } else {
      // Error in request setup
      console.error('API Request Setup Error:', error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * Set authentication token for all requests
 * @param token
 */
export const setAuthToken = (token: string | null) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

// Task API functions

/**
 * Health check - Verify API connectivity
 */
export const healthCheck = async (): Promise<boolean> => {
  try {
    const response = await apiClient.get('/health');
    return response.data.success === true;
  } catch (error) {
    console.error('Health check failed:', error);
    return false;
  }
};

/**
 * Get all tasks with optional filtering and sorting (enhanced)
 * @param queryParams
 */
export const getTasks = async (queryParams?: TaskQueryParams): Promise<Task[]> => {
  const response = await apiClient.get<{ success: boolean; data: Task[] }>('/tasks', {
    params: queryParams,
  });
  return response.data.data;
};

/**
 * Get a single task by ID
 * @param id
 */
export const getTaskById = async (id: string): Promise<Task> => {
  const response = await apiClient.get<{ success: boolean; data: Task }>(`/tasks/${id}`);
  return response.data.data;
};

/**
 * Create a new task (enhanced with new fields)
 * @param task
 */
export const createTask = async (task: CreateTaskInput): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>('/tasks', task);
  return response.data.data;
};

/**
 * Update an existing task (enhanced with new fields)
 * @param id
 * @param task
 */
export const updateTask = async (id: string, task: UpdateTaskInput): Promise<Task> => {
  const response = await apiClient.put<{ success: boolean; data: Task }>(`/tasks/${id}`, task);
  return response.data.data;
};

/**
 * Delete a task
 * @param id
 */
export const deleteTask = async (id: string): Promise<void> => {
  await apiClient.delete(`/tasks/${id}`);
};

/**
 * Get upcoming alarms for the authenticated user (super branch feature)
 * @param hoursAhead
 */
export const getUpcomingAlarms = async (hoursAhead: number = 24): Promise<AlarmInfo[]> => {
  const response = await apiClient.get<{ success: boolean; data: AlarmInfo[] }>('/tasks/alarms', {
    params: { hours: hoursAhead },
  });
  return response.data.data;
};

/**
 * Archive a task (super branch feature)
 * @param id
 */
export const archiveTask = async (id: string): Promise<Task> => {
  const response = await apiClient.patch<{ success: boolean; data: Task }>(`/tasks/${id}/archive`);
  return response.data.data;
};

/**
 * Unarchive a task (super branch feature)
 * @param id
 */
export const unarchiveTask = async (id: string): Promise<Task> => {
  const response = await apiClient.patch<{ success: boolean; data: Task }>(`/tasks/${id}/unarchive`);
  return response.data.data;
};

/**
 * Upload a file to Supabase Storage (super branch feature)
 * @param file - File to upload
 * @param taskId - Task ID for organizing files
 */
export const uploadAttachment = async (file: File, taskId: string): Promise<string> => {
  // TODO: Implement Supabase Storage upload
  // For now, return a placeholder URL
  console.warn('File not yet implemented:', file.name, taskId);
  return `https://placeholder.com/${taskId}/${file.name}`;
};

/**
 * Upload profile photo to Supabase Storage
 * @param file - Image file to upload
 * @param userId - User ID
 */
export const uploadProfilePhoto = async (file: File, userId: string): Promise<string> => {
  const response = await apiClient.post<{ success: boolean; data: { url: string } }>(
    '/profile/upload-photo',
    { file, userId },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data.data.url;
};

/**
 * Get user profile
 * @param userId - User ID
 */
export const getProfile = async (userId: string): Promise<{ username: string; avatar_url: string | null }> => {
  const response = await apiClient.get<{ success: boolean; data: { username: string; avatar_url: string | null } }>(
    `/profile/${userId}`
  );
  return response.data.data;
};

/**
 * Update user profile
 * @param userId - User ID
 * @param data - Profile data to update
 */
export const updateProfile = async (
  userId: string,
  data: { username?: string; avatar_url?: string }
): Promise<{ username: string; avatar_url: string | null }> => {
  const response = await apiClient.put<{ success: boolean; data: { username: string; avatar_url: string | null } }>(
    `/profile/${userId}`,
    data
  );
  return response.data.data;
};

export default apiClient;

